import { useEffect } from "react";
import brand from "lib/brand";

/**
 * Meta tags da pagina no NAVEGADOR.
 *
 * O `index.html` traz um titulo e uma descricao fixos (o nome da marca), que
 * valem para a home mas nao dizem nada numa galeria de evento. Como o SPA nao
 * e renderizado no servidor, quem escreve as tags de cada tela e o proprio
 * React, ao montar.
 *
 * Isto NAO substitui a previa de compartilhamento: crawler de rede social nao
 * executa JavaScript, entao WhatsApp, Facebook e X continuam sendo servidos
 * pelo backend (`views/public.py: EventShareView` / `PhotoShareView`, desvio
 * por User-Agent no nginx). O que se ganha aqui e o que o visitante, o
 * navegador e os buscadores que executam JS enxergam.
 */

// Tags que este modulo administra. Manter alinhado com o `index.html` e com
// os templates de compartilhamento — as tres pecas descrevem a mesma pagina.
const MANAGED = [
  ["name", "description"],
  ["property", "og:type"],
  ["property", "og:site_name"],
  ["property", "og:title"],
  ["property", "og:description"],
  ["property", "og:url"],
  ["property", "og:image"],
  ["name", "twitter:card"],
  ["name", "twitter:title"],
  ["name", "twitter:description"],
  ["name", "twitter:image"],
];

function upsert(attribute, key, content) {
  const selector = `meta[${attribute}="${key}"]`;
  let tag = document.head.querySelector(selector);
  if (!content) {
    // Sem valor, a tag sai: melhor ausente do que anunciando vazio.
    if (tag && tag.dataset.pageMeta === "1") tag.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  // Marca as que criamos/alteramos, para a limpeza saber o que e nosso.
  tag.dataset.pageMeta = "1";
  tag.setAttribute("content", content);
}

/** Estado inicial do documento — o que restauramos ao sair da tela. */
const DEFAULTS = {
  title: typeof document !== "undefined" ? document.title : brand.name,
  description:
    typeof document !== "undefined"
      ? document.head.querySelector('meta[name="description"]')?.content ||
        brand.name
      : brand.name,
};

/**
 * Aplica titulo e meta tags enquanto a tela estiver montada.
 *
 * `meta` pode chegar vazio (`null`) na primeira renderizacao, enquanto os
 * dados carregam — nesse caso nada e escrito e o documento fica no default.
 */
export function usePageMeta(meta) {
  // Serializado: o objeto e recriado a cada render e dispararia o efeito
  // eternamente se entrasse na lista de dependencias como referencia.
  const key = JSON.stringify(meta || null);

  useEffect(() => {
    const data = key ? JSON.parse(key) : null;
    if (!data) return undefined;

    const title = data.title ? `${data.title} — ${brand.name}` : DEFAULTS.title;
    const description = data.description || DEFAULTS.description;
    const url = data.url || window.location.href;

    document.title = title;
    upsert("name", "description", description);
    upsert("property", "og:type", data.type || "website");
    upsert("property", "og:site_name", brand.name);
    upsert("property", "og:title", data.title || DEFAULTS.title);
    upsert("property", "og:description", description);
    upsert("property", "og:url", url);
    upsert("property", "og:image", data.image);
    upsert("name", "twitter:card", data.image ? "summary_large_image" : "summary");
    upsert("name", "twitter:title", data.title || DEFAULTS.title);
    upsert("name", "twitter:description", description);
    upsert("name", "twitter:image", data.image);

    return () => {
      document.title = DEFAULTS.title;
      MANAGED.forEach(([attribute, name]) => {
        const tag = document.head.querySelector(
          `meta[${attribute}="${name}"][data-page-meta="1"]`
        );
        if (tag) tag.remove();
      });
      // A descricao existe no `index.html`: some do DOM se a removermos, entao
      // e reposta com o valor original em vez de apagada.
      upsert("name", "description", DEFAULTS.description);
    };
  }, [key]);
}

export default usePageMeta;
